// @oasis-permission-inherits: /ai-edit-tool (ai-edit-v2-tool:view)
import { Icon } from "@mirage-web/icons";
import { skipToken } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "~/components/ui/button";
import type { OverlayItem } from "~/modules/ai-edit-tool/components/shot-style-form";
import { ShotStyleForm } from "~/modules/ai-edit-tool/components/shot-style-form";
import { ShotStyleModeIndicator } from "~/modules/ai-edit-tool/components/shot-style-mode-indicator";
import { ShotStylePreviewPanel } from "~/modules/ai-edit-tool/components/shot-style-preview-panel";
import {
  DisplayCategory,
  displayCategoryFromProto,
  DisplayCategoryStringToNum,
  type DisplayCategoryValue,
  ShotType,
  shotTypeFromProto,
  type ShotTypeValue,
  type SoundEffectConfig,
} from "~/modules/ai-edit-tool/constants";
import { useStyleEditorPermissions } from "~/modules/ai-edit-tool/hooks/use-style-editor-permissions";
import { convertOverlaysToApiFormat } from "~/modules/ai-edit-tool/lib/converters";
import { hasInvalidAiTitleLayerReferences } from "~/modules/ai-edit-tool/lib/overlay-validation";
import {
  createCaptions,
  createSoundEffect,
  type CaptionLayout,
} from "~/modules/ai-edit-tool/lib/template-builder";
import {
  parseCaptionColorOverrides,
  parseCaptionLayout,
  parseCaptionOverrideTemplateId,
  parseOverlays,
  parseSoundEffects,
} from "~/modules/ai-edit-tool/lib/template-parser";
import {
  addShotStyleToVideoStyle,
  upsertAiEditShotStyle,
} from "~/modules/ai-edit-tool/network/mutations";
import { getAiEditShotStyles, getAiEditStyles } from "~/modules/ai-edit-tool/network/queries";
import {
  VideoType,
  type CaptionsColorOverrideConfig,
  strokeStyleSchema,
  type StrokeStyle,
} from "~/modules/ai-edit-tool/schemas";
const searchParamsSchema = z.object({
  mode: z.enum(["view", "edit"]).default("view"),
  videoStyleId: z.string(),
});

export const Route = createFileRoute("/_dash/ai-edit-tool/sst/$shotStyleId")({
  component: RouteComponent,
  validateSearch: searchParamsSchema,
});

function RouteComponent() {
  const { canEditStyle } = useStyleEditorPermissions();
  const navigate = useNavigate();
  const { shotStyleId } = Route.useParams();
  const { mode, videoStyleId } = Route.useSearch();

  const canEditVideoStyle = canEditStyle(videoStyleId);
  const isReadOnly = mode === "view" || !canEditVideoStyle;

  const { data: videoStylesData } = getAiEditStyles.useQuery({
    params: {
      client: "DESKTOP",
      version: "dev",
      afterCursor: "",
      tags: [],
      pageSize: 150,
    },
  });
  const videoStyle = videoStylesData?.styles?.page.find((style) => style.id === videoStyleId);

  const { data: shotStylesData, isLoading: isShotStylesLoading } = getAiEditShotStyles.useQuery(
    videoStyleId
      ? {
          params: { videoStyleId },
        }
      : skipToken
  );

  const shotStyle = useMemo(() => {
    return shotStylesData?.shotTemplates.find((sst) => sst.id === shotStyleId);
  }, [shotStylesData, shotStyleId]);

  const shotStyles = useMemo(() => {
    return shotStylesData?.shotTemplates || [];
  }, [shotStylesData]);

  const currentShotStyleIndex = useMemo(() => {
    return shotStyles.findIndex((sst) => sst.id === shotStyleId);
  }, [shotStyles, shotStyleId]);

  const hasPrevious = currentShotStyleIndex > 0;
  const hasNext = currentShotStyleIndex >= 0 && currentShotStyleIndex < shotStyles.length - 1;

  const handleNavigateToPrevious = useCallback(() => {
    if (hasPrevious && shotStyles[currentShotStyleIndex - 1]) {
      navigate({
        to: "/ai-edit-tool/sst/$shotStyleId",
        params: { shotStyleId: shotStyles[currentShotStyleIndex - 1].id },
        search: { mode, videoStyleId },
      });
    }
  }, [hasPrevious, shotStyles, currentShotStyleIndex, navigate, mode, videoStyleId]);

  const handleNavigateToNext = useCallback(() => {
    if (hasNext && shotStyles[currentShotStyleIndex + 1]) {
      navigate({
        to: "/ai-edit-tool/sst/$shotStyleId",
        params: { shotStyleId: shotStyles[currentShotStyleIndex + 1].id },
        search: { mode, videoStyleId },
      });
    }
  }, [hasNext, shotStyles, currentShotStyleIndex, navigate, mode, videoStyleId]);

  const [displayName, setDisplayName] = useState("");
  const [shotType, setShotType] = useState<ShotTypeValue>(ShotType.SHOT_TYPE_UNSPECIFIED);
  const [displayCategory, setDisplayCategory] = useState<DisplayCategoryValue>(
    DisplayCategory.CATEGORY_UNSPECIFIED
  );
  const [supportedVideoTypes, setSupportedVideoTypes] = useState<VideoType[]>([]);
  const [isDefaultShotStyle, setIsDefaultShotStyle] = useState(false);
  const [initialIsDefaultShotStyle, setInitialIsDefaultShotStyle] = useState(false);

  const [captionLayout, setCaptionLayout] = useState<CaptionLayout>("lower");
  const [captionOverrideTemplateId, setCaptionOverrideTemplateId] = useState("");
  const [captionColorOverrides, setCaptionColorOverrides] = useState<CaptionsColorOverrideConfig[]>(
    []
  );

  const [foregroundOverlays, setForegroundOverlays] = useState<OverlayItem[]>([]);
  const [backgroundOverlays, setBackgroundOverlays] = useState<OverlayItem[]>([]);

  const [soundEffectConfigs, setSoundEffectConfigs] = useState<SoundEffectConfig[]>([]);

  const [thumbnailUrl, setThumbnailUrl] = useState("");

  const [strokeStyle, setStrokeStyle] = useState<StrokeStyle | undefined>(undefined);

  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    if (shotStyle && !hasInitialized) {
      const rawData = shotStyle as unknown as Record<string, unknown>;

      setDisplayName(shotStyle.displayName || "");
      setShotType(shotTypeFromProto(shotStyle.metadata?.shotType));
      setDisplayCategory(displayCategoryFromProto(shotStyle.metadata?.category));
      setSupportedVideoTypes(shotStyle.metadata?.supportedVideoTypes || []);

      const isDefault = shotStylesData?.defaultShotStyleId === shotStyleId;
      setIsDefaultShotStyle(isDefault);
      setInitialIsDefaultShotStyle(isDefault);

      setCaptionLayout(parseCaptionLayout(rawData.captions as Record<string, unknown>));
      setCaptionOverrideTemplateId(
        parseCaptionOverrideTemplateId(rawData.captions as Record<string, unknown>)
      );
      setCaptionColorOverrides(
        parseCaptionColorOverrides(rawData.captions as Record<string, unknown>)
      );

      setForegroundOverlays(
        parseOverlays(rawData.foregroundOverlays as Array<Record<string, unknown>>)
      );
      setBackgroundOverlays(
        parseOverlays(rawData.backgroundOverlays as Array<Record<string, unknown>>)
      );

      setSoundEffectConfigs(
        parseSoundEffects(rawData.soundEffects as Array<Record<string, unknown>>)
      );

      setThumbnailUrl(shotStyle.metadata?.thumbnailUrl || "");

      const parsedStrokeStyle = strokeStyleSchema.safeParse(rawData.strokeStyle);
      setStrokeStyle(parsedStrokeStyle.success ? parsedStrokeStyle.data : undefined);

      setHasInitialized(true);
    }
  }, [shotStyle, hasInitialized, shotStylesData, shotStyleId]);

  useEffect(() => {
    setHasInitialized(false);
  }, [shotStyleId]);

  const { mutateAsync: upsertShotStyle, isPending: isUpsertPending } =
    upsertAiEditShotStyle.useMutation({
      onError: () => {
        toast.error("Failed to update shot style");
      },
    });

  const { mutateAsync: updateDefaultShotStyle, isPending: isDefaultPending } =
    addShotStyleToVideoStyle.useMutation({
      onError: () => {
        toast.error("Failed to update default shot style");
      },
    });

  const isPending = isUpsertPending || isDefaultPending;

  const onSubmit = useCallback(async () => {
    if (isReadOnly) {
      return;
    }

    const hasInvalidAiTextLayerRefs =
      hasInvalidAiTitleLayerReferences(foregroundOverlays) ||
      hasInvalidAiTitleLayerReferences(backgroundOverlays);

    if (hasInvalidAiTextLayerRefs) {
      toast.error("Each AI text slot must include at least one valid layer index");
      return;
    }

    const captions = createCaptions(
      captionLayout,
      captionOverrideTemplateId || undefined,
      captionColorOverrides
    );
    const convertedForegroundOverlays = convertOverlaysToApiFormat(foregroundOverlays);
    const convertedBackgroundOverlays = convertOverlaysToApiFormat(backgroundOverlays);
    const soundEffects = soundEffectConfigs.map((config) =>
      createSoundEffect(config.assetId, config.volume, config.mode)
    );

    const payload = {
      id: shotStyleId,
      shotTemplate: {
        id: shotStyleId,
        displayName,
        metadata: {
          shotType,
          category: DisplayCategoryStringToNum[displayCategory] ?? 0,
          supportedVideoTypes,
          thumbnailUrl: thumbnailUrl || undefined,
          ios: { inDev: true },
          desktop: { inDev: true },
          android: { inDev: true },
        },
        captions,
        foregroundOverlays: convertedForegroundOverlays,
        backgroundOverlays: convertedBackgroundOverlays,
        soundEffects,
        ...(strokeStyle ? { strokeStyle } : {}),
      },
    };

    await upsertShotStyle({
      params: payload,
    });

    if (isDefaultShotStyle !== initialIsDefaultShotStyle) {
      await updateDefaultShotStyle({
        params: {
          videoStyleId,
          shotStyleId,
          isDefaultShotStyle,
        },
      });
    }

    toast.success("Shot style updated successfully");
    navigate({
      to: "/ai-edit-tool/sst/$shotStyleId",
      params: { shotStyleId },
      search: { mode: "view", videoStyleId },
    });
  }, [
    displayName,
    shotType,
    displayCategory,
    supportedVideoTypes,
    captionLayout,
    captionOverrideTemplateId,
    captionColorOverrides,
    foregroundOverlays,
    backgroundOverlays,
    soundEffectConfigs,
    thumbnailUrl,
    strokeStyle,
    shotStyleId,
    videoStyleId,
    isDefaultShotStyle,
    initialIsDefaultShotStyle,
    isReadOnly,
    upsertShotStyle,
    updateDefaultShotStyle,
    navigate,
  ]);

  const handleEditClick = useCallback(() => {
    navigate({
      to: "/ai-edit-tool/sst/$shotStyleId",
      params: { shotStyleId },
      search: { mode: "edit", videoStyleId },
    });
  }, [navigate, shotStyleId, videoStyleId]);

  const handleCancelEdit = useCallback(() => {
    setHasInitialized(false);
    navigate({
      to: "/ai-edit-tool/sst/$shotStyleId",
      params: { shotStyleId },
      search: { mode: "view", videoStyleId },
    });
  }, [navigate, shotStyleId, videoStyleId]);

  if (isShotStylesLoading) {
    return (
      <div className="flex flex-col gap-4 h-full">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() =>
              navigate({
                to: "/ai-edit-tool",
                search: { videoStyleId, tab: "sst" },
              })
            }
            size="sm"
          >
            {"< Back"}
          </Button>
          <span className="text-sm text-muted-foreground font-mono">Loading...</span>
        </div>
      </div>
    );
  }

  if (!shotStyle) {
    return (
      <div className="flex flex-col gap-4 h-full">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() =>
              navigate({
                to: "/ai-edit-tool",
                search: { videoStyleId, tab: "sst" },
              })
            }
            size="sm"
          >
            {"< Back"}
          </Button>
          <span className="text-sm text-destructive font-mono">Shot style not found</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 h-full">
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          onClick={() =>
            navigate({
              to: "/ai-edit-tool",
              search: { videoStyleId, tab: "sst" },
            })
          }
          size="sm"
        >
          {"< Back"}
        </Button>
        <Button
          variant="outline"
          onClick={handleNavigateToPrevious}
          disabled={!hasPrevious || isReadOnly === false}
          size="sm"
          className="px-2"
          title={isReadOnly ? "Previous shot style" : "Save changes to navigate"}
        >
          <Icon name="chevron-left" size={16} />
        </Button>
        <Button
          variant="outline"
          onClick={handleNavigateToNext}
          disabled={!hasNext || isReadOnly === false}
          size="sm"
          className="px-2"
          title={isReadOnly ? "Next shot style" : "Save changes to navigate"}
        >
          <Icon name="chevron-right" size={16} />
        </Button>
        <ShotStyleModeIndicator
          mode={mode}
          shotStyleName={displayName || shotStyle.displayName}
          canEdit={canEditVideoStyle}
          onEditClick={handleEditClick}
          onCancelEdit={handleCancelEdit}
        />
        {!isReadOnly && (
          <Button
            onClick={onSubmit}
            disabled={isPending || !displayName}
            size="sm"
            className="ml-auto"
          >
            {isPending ? "Saving..." : "Save"}
          </Button>
        )}
      </div>

      <div className="text-xs text-muted-foreground font-mono">
        Video Style: {videoStyle?.displayName || videoStyleId} ({videoStyleId})
      </div>

      <div className="flex-1 min-h-0 grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
        <ShotStyleForm
          videoStyleId={videoStyleId}
          shotStyleId={shotStyleId}
          basicInfo={{
            displayName,
            shotType,
            displayCategory,
            supportedVideoTypes,
            isDefaultShotStyle,
          }}
          onBasicInfoChange={(info) => {
            setDisplayName(info.displayName);
            setShotType(info.shotType);
            setDisplayCategory(info.displayCategory);
            setSupportedVideoTypes(info.supportedVideoTypes);
            setIsDefaultShotStyle(info.isDefaultShotStyle);
          }}
          captions={{
            captionLayout,
            captionOverrideTemplateId,
            captionColorOverrides,
          }}
          onCaptionsChange={(captions) => {
            setCaptionLayout(captions.captionLayout);
            setCaptionOverrideTemplateId(captions.captionOverrideTemplateId);
            setCaptionColorOverrides(captions.captionColorOverrides || []);
          }}
          strokeStyle={strokeStyle}
          onStrokeStyleChange={setStrokeStyle}
          foregroundOverlays={foregroundOverlays}
          onForegroundOverlaysChange={setForegroundOverlays}
          backgroundOverlays={backgroundOverlays}
          onBackgroundOverlaysChange={setBackgroundOverlays}
          soundEffects={soundEffectConfigs}
          onSoundEffectsChange={setSoundEffectConfigs}
          readOnly={isReadOnly}
          jsonData={shotStyle}
          thumbnailUrl={thumbnailUrl || shotStyle.metadata?.thumbnailUrl}
          onThumbnailUrlChange={setThumbnailUrl}
        />
        <aside className="min-w-0">
          <ShotStylePreviewPanel
            shotStyle={shotStyle as Parameters<typeof ShotStylePreviewPanel>[0]["shotStyle"]}
            videoStyleCaptionsTemplate={
              videoStyle?.captionsTemplate as Parameters<
                typeof ShotStylePreviewPanel
              >[0]["videoStyleCaptionsTemplate"]
            }
            videoStyleColorPresets={videoStyle?.colorPresets}
            videoStyleCaptionsColorOverrides={videoStyle?.captionsColorOverrideConfigs}
            supportedAspectRatios={videoStyle?.supportedAspectRatios}
          />
        </aside>
      </div>
    </div>
  );
}
